import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { api } from "../lib/api";

// Tiny inline sparkline for the breadth trend. Kept here (not reused from
// /Sparkline.jsx) because we want a center-anchored 50% baseline so the
// shape visually communicates "above 50" vs "below 50" rather than just
// min/max range.
function BreadthSparkline({ history }) {
  if (!history || history.length < 2) return null;
  const w = 90;
  const h = 22;
  const vals = history.map((p) => p.bullish_pct);
  const minV = Math.min(...vals, 45);
  const maxV = Math.max(...vals, 55);
  const range = maxV - minV || 1;
  const step = w / (vals.length - 1);
  const y = (v) => h - ((v - minV) / range) * h;
  const pts = vals.map((v, i) => `${(i * step).toFixed(2)},${y(v).toFixed(2)}`).join(" ");
  const last = vals[vals.length - 1];
  const first = vals[0];
  const positive = last >= first;
  const color = positive ? "hsl(160 84% 45%)" : "hsl(0 84% 60%)";
  const baselineY = y(50);
  return (
    <svg width={w} height={h} className="block shrink-0" data-testid="breadth-sparkline">
      {/* 50% baseline */}
      <line x1={0} x2={w} y1={baselineY} y2={baselineY}
            stroke="currentColor" strokeOpacity="0.25" strokeDasharray="2 3" />
      <polyline fill="none" stroke={color} strokeWidth="1.4" points={pts} />
      <circle cx={(vals.length - 1) * step} cy={y(last)} r="1.8" fill={color} />
    </svg>
  );
}

/**
 * Horizontal split breadth meter — left half (green) = bullish %, right half
 * (red) = bearish %. Computed server-side from the existing top_movers cache
 * filtered to NIFTY 500 universe. Auto-refreshes every 5 minutes.
 *
 * Designed to slot under the KPI strip on the dashboard — single line on
 * desktop, comfortable two-line stack on mobile. No external libraries.
 */
export default function MarketBreadthMeter() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    let mounted = true;
    const load = () => {
      api
        .get("/market-breadth")
        .then(({ data: d }) => { if (mounted) { setData(d); setErr(false); } })
        .catch(() => { if (mounted) setErr(true); });
    };
    load();
    const id = setInterval(load, 60 * 1000); // 1-min refresh so sparkline accumulates
    return () => { mounted = false; clearInterval(id); };
  }, []);

  if (err && !data) return null; // silent on first fetch fail; KPI strip already covers basic state
  const bullPct = data?.bullish_pct ?? 50;
  const bearPct = data?.bearish_pct ?? 50;
  const bullish = data?.bullish ?? 0;
  const bearish = data?.bearish ?? 0;
  // Skew the dominant side visual hint: brighter ring on the leading side.
  const leadingBull = bullPct >= bearPct;

  return (
    <div
      data-testid="market-breadth-meter"
      className="border-b border-subtle bg-surface px-4 md:px-6 py-3"
    >
      <div className="flex items-center gap-3 md:gap-5 flex-wrap">
        <div className="flex items-center gap-2 shrink-0">
          <span className="text-[10px] font-mono uppercase tracking-[0.22em] text-muted-fg">
            Market Breadth
          </span>
          <span className="text-[10px] font-mono uppercase tracking-widest text-muted-fg/70">
            · NIFTY 500
          </span>
          {data?.history?.length > 1 && (
            <span className="text-muted-fg/60 hidden md:block">
              <BreadthSparkline history={data.history} />
            </span>
          )}
        </div>

        <div className="flex-1 min-w-[200px] flex items-center gap-3">
          {/* Bullish label */}
          <div className="flex items-baseline gap-1.5 shrink-0">
            <span
              data-testid="breadth-bull-pct"
              className={
                "font-mono text-base md:text-lg tabular-nums " +
                (leadingBull ? "text-pos" : "text-pos/80")
              }
            >
              {bullPct.toFixed(1)}%
            </span>
            <span className="text-[10px] font-mono text-muted-fg tabular-nums">
              {bullish}↑
            </span>
          </div>

          {/* The bar — split visually so both green (bullish) and red
              (bearish) segments are proportional to their share. */}
          <div
            className="relative flex-1 h-2.5 rounded-sm overflow-hidden border border-subtle bg-app/40"
            role="img"
            aria-label={`${bullPct.toFixed(1)}% bullish, ${bearPct.toFixed(1)}% bearish`}
          >
            <motion.div
              className="absolute left-0 top-0 bottom-0 bg-pos"
              initial={{ width: 0 }}
              animate={{ width: `${bullPct}%` }}
              transition={{ duration: 0.6, ease: "easeOut" }}
              data-testid="breadth-bull-fill"
            />
            <motion.div
              className="absolute right-0 top-0 bottom-0 bg-neg"
              initial={{ width: 0 }}
              animate={{ width: `${bearPct}%` }}
              transition={{ duration: 0.6, ease: "easeOut" }}
              data-testid="breadth-bear-fill"
            />
            {/* Midline marker (50%) sits on top so it stays visible against
                whichever colour dominates. */}
            <div className="absolute top-0 bottom-0 left-1/2 w-px bg-app/70 z-10" />
          </div>

          {/* Bearish label */}
          <div className="flex items-baseline gap-1.5 shrink-0">
            <span className="text-[10px] font-mono text-muted-fg tabular-nums">
              {bearish}↓
            </span>
            <span
              data-testid="breadth-bear-pct"
              className={
                "font-mono text-base md:text-lg tabular-nums " +
                (!leadingBull ? "text-neg" : "text-neg/80")
              }
            >
              {bearPct.toFixed(1)}%
            </span>
          </div>
        </div>

        <div className="text-[10px] font-mono uppercase tracking-widest text-muted-fg/70 shrink-0">
          {data?.stale ? (
            <span className="text-mod">stale</span>
          ) : (
            <>{data?.ist_time ? data.ist_time.split(" ")[1] : "—"} IST</>
          )}
        </div>
      </div>
    </div>
  );
}
